import { useEffect, useRef, useState } from 'react';

export function useChangedFlag<T>(value: T, durationMs = 5000) {
    const lastValue = useRef<T>(value);
    const hasInitialized = useRef(!!value); // true if initial value is truthy
    const [changed, setChanged] = useState(false);

    // Detect value changes and set the flag
    useEffect(() => {
        if (value !== lastValue.current) {
            // Only trigger if we've already seen a truthy value before
            const shouldTrigger = hasInitialized.current;

            lastValue.current = value;

            // Mark as initialized once we see a truthy value
            if (value) {
                hasInitialized.current = true;
            }

            if (shouldTrigger) {
                setChanged(true);
            }
        }
    }, [value]);

    // Manage the timeout separately - clears properly via cleanup
    useEffect(() => {
        if (!changed) return;

        const timeout = window.setTimeout(() => {
            setChanged(false);
        }, durationMs);

        return () => clearTimeout(timeout);
    }, [changed, durationMs]);

    return changed;
}
